//
// TextListMetadataField
//
// A list of text-inputs.

import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { DragDropClasslistFieldRow } from '../../dragdrop/DragDropClasslistFieldRow'
import { useClickOutside } from '../../hooks/useClickOutside'
import { useMousePopover } from '../../hooks/useMousePopover'
import { useStore } from '../../stores'

import { TextListMenu } from '../../menus'

import { keyboard } from '../../utils/keyboard'
import {
    addIndexedListValue,
    moveIndexedListValue,
    removeIndexedListValue,
    replaceIndexedListValue,
} from '../../utils/list'

import { Icon, TextInput } from '../../components'

export const TextListMetadataField = observer(function TextListMetadataField(props) {
    const {
        className,
        field,
        fieldname,
        fieldpath,
        item,
        language,
        renderkey,
        onChange,
        ...other
    } = props

    const { app } = useStore()
    const containerRef = useRef()

    const TextListContextMenu = useMousePopover(TextListMenu)
    const [focusItem, setFocusItem] = useState([null, 'end'])

    let classes = 'cc-Field cc-TextListField cc-TextListMetadataField ws-table'
    if (className) classes += ' ' + className

    const fieldvalue = field.is_localized ? field.value[language] : field.value

    const [selectedItemIndex, setSelectedItemIndex] = useState(null) // an index
    useClickOutside(containerRef, () => setSelectedItemIndex(null))

    const onAddItemBefore = before_index => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = addIndexedListValue(
            metadatafield.is_localized
                ? metadatafield.value[language]
                : metadatafield.value,
            '',
            before_index
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
        if (before_index === null) {
            setFocusItem([newValue.length - 1, 'start'])
        } else {
            setFocusItem([before_index, 'start'])
        }
    }

    const onRemoveItem = index => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = removeIndexedListValue(
            metadatafield.is_localized
                ? metadatafield.value[language]
                : metadatafield.value,
            index
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
        setFocusItem([index - 1, 'end'])
    }

    const onRemoveAllItems = () => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = []
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
        setFocusItem([null, 'end'])
    }

    const onTextListAction = action => {
        const actions = {
            add_item: onAddItemBefore,
            remove_item: onRemoveItem,
            remove_all: onRemoveAllItems,
        }
        if (!(action in actions)) {
            console.log(`onTextListAction: unhandled action '${action}'`)
            return
        }
        TextListContextMenu.hide()
        actions[action](selectedItemIndex)
    }

    const onDrop = (dragitem, item_index, isoverzone) => {
        const other_index = dragitem.id
        let before_index = null
        if (['top', 'left', 'inside'].includes(isoverzone)) {
            before_index = item_index
        } else {
            // before_index is record -after- item_index
            before_index = item_index + 1
        }
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = moveIndexedListValue(
            metadatafield.is_localized
                ? metadatafield.value[language]
                : metadatafield.value,
            other_index,
            before_index
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
        setFocusItem([null, 'end'])
    }

    const onChangeLine = (index, newItemValue) => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = replaceIndexedListValue(
            metadatafield.is_localized
                ? metadatafield.value[language]
                : metadatafield.value,
            newItemValue,
            index
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
    }

    const onBlurLine = (index, newItemValue) => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = replaceIndexedListValue(
            metadatafield.is_localized
                ? metadatafield.value[language]
                : metadatafield.value,
            newItemValue,
            index
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
    }

    const onUpdateAddAfter = (index, newItemValue) => {
        const metadatafield = item.metadata.get(fieldpath)
        const newValue = addIndexedListValue(
            replaceIndexedListValue(
                metadatafield.is_localized
                    ? metadatafield.value[language]
                    : metadatafield.value,
                newItemValue,
                index
            ),
            '',
            index + 1
        )
        item.setMetadataField(
            fieldpath,
            metadatafield.type,
            metadatafield.is_localized,
            newValue,
            language
        )
        onChange && onChange(item)
        setFocusItem([index + 1, 'start'])
    }

    const onShowTextListMenu = (e, index) => {
        if (index === null) {
            onAddItemBefore(null)
            return
        }
        setSelectedItemIndex(index)
        TextListContextMenu.onShow(e)
    }

    let TableRows
    if (fieldvalue && fieldvalue.length && fieldvalue.map) {
        TableRows = fieldvalue.map((line, index) => {
            let cellclasses = 'ws-table-cell ws-table-cell-text'
            const _onChange = event => {
                onChangeLine && onChangeLine(index, event.target.value)
            }
            const _onBlur = event => {
                onBlurLine && onBlurLine(index, event.target.value.trim())
            }
            const _onKeyDown = event => {
                if (keyboard.test(event, keyboard.ENTER)) {
                    onUpdateAddAfter(index, event.target.value.trim())
                } else if (
                    (!event.target.value.length || event.target.value === '\n') &&
                    keyboard.test(event, keyboard.BACKSPACE)
                ) {
                    onRemoveItem(index)
                }
            }
            const inputrenderkey = renderkey + index.toString()

            let Input = (
                <TextInput
                    multiline={false}
                    enabled={true}
                    setFocus={index === focusItem[0] ? focusItem[1] : false}
                    value={line}
                    language={language}
                    onChange={_onChange}
                    onBlur={_onBlur}
                    onKeyDown={_onKeyDown}
                    renderkey={inputrenderkey}
                    {...other}
                />
            )

            return (
                <DragDropClasslistFieldRow
                    key={
                        'field' +
                        fieldpath +
                        '.' +
                        fieldvalue.length +
                        '.' +
                        index.toString()
                    }
                    className={classes}
                    field={field}
                    item_index={index}
                    direction="vertical"
                    disabled={false}
                    onDrop={onDrop}
                >
                    <td
                        key={'action'}
                        className="ws-table-cell ws-table-cell-_action_"
                        onClick={e => onShowTextListMenu(e, index)}
                        onContextMenu={e => onShowTextListMenu(e, index)}
                    >
                        <Icon size="text" name="ellipsis" />
                    </td>
                    <td className={cellclasses}>{Input}</td>
                    <td key={'handle'} className="ws-table-cell ws-table-cell-_action_">
                        <Icon size={1} name="reorder" />
                    </td>
                </DragDropClasslistFieldRow>
            )
        })
    }

    let addtablerowclasses = 'ws-table-cell cc-dimmed'
    const AddTableRow = (
        <tr key={'field' + field.gid + '.add'}>
            <td className={addtablerowclasses + ' ws-table-cell-_action_'}>
                <Icon size={1} name="plus" onClick={e => onAddItemBefore(null)} />
            </td>
            <td
                colSpan={2}
                className={addtablerowclasses + ' ws-table-cell-_add-text_'}
                style={{ textAlign: 'left' }}
            >
                <div onClick={e => onAddItemBefore(null)}>
                    {app.text('Add new item')}
                </div>
            </td>
        </tr>
    )

    if (!TableRows) {
        TableRows = AddTableRow
        classes += ' cc-no-items'
    } else {
        TableRows.push(AddTableRow)
    }

    return (
        <div>
            <TextListContextMenu.Panel onAction={onTextListAction} />
            <div className={classes} {...other}>
                <table className="ws-table">
                    <tbody ref={containerRef}>{TableRows}</tbody>
                </table>
            </div>
        </div>
    )
})
